// キャンバス上のフローティングパネルの実測サイズを CSS 変数へ反映する汎用フック。
//
// 複数のフローティングパネル（左上アプリメニュー・下部中央ツールバー・右上コントロール）は
// 互いに重なり得るため、隣のパネルが占める領域を差し引いた幅・高さを CSS 側の上限に使う。
// 差し引き量を「最大構成の見積もり」で固定すると、実際のパネルが小さい場合でも同じだけ引かれ、
// 画面に余裕があるのに折り返したり短くなったりしてしまう。実測値を渡すことで、
// 制限を「本当に余裕が無いとき」だけに限定する。
// 画面右下の下部コントロール（全体マップ・ズームパネル・FAB 用の空き領域）の実測高さも同じ仕組みで
// 渡し、画面右下のお知らせをその上に出すために使う（下部コントロールの高さは、ルート文字サイズで
// 全体マップの大きさが変わるほか、狭い表示で全体マップを隠したときにも変わるため）。
//
// 表示内容（参加者数・省略表示・サブパネルの開閉）で寸法が変わるため、ResizeObserver で追従する。

import { useLayoutEffect, type RefObject } from "react";

/** 右上コントロール（参加者一覧・共有タイマー）の実測幅。 */
export const TOP_RIGHT_WIDTH_VARIABLE = "--canvas-top-right-width";
/** 左上アプリメニューの実測高さ。 */
export const APP_MENU_HEIGHT_VARIABLE = "--canvas-app-menu-height";
/** 下部中央ツールバー本体の実測幅。 */
export const BOTTOM_TOOLBAR_WIDTH_VARIABLE = "--canvas-bottom-toolbar-width";
/** 画面右下の下部コントロール（全体マップ + ズームパネル + FAB 用の空き領域）の実測高さ。 */
export const BOTTOM_CONTROLS_HEIGHT_VARIABLE = "--canvas-bottom-controls-height";

/** 実測して CSS 変数へ渡す辺。 */
export type PanelSizeDimension = "width" | "height";

export function usePanelSizeReserve(
  ref: RefObject<HTMLElement | null>,
  variable: string,
  dimension: PanelSizeDimension
): void {
  // useEffect ではなく useLayoutEffect を使う。useEffect だと初回は CSS 側の既定値で 1 フレーム
  // 描画してから実測値で跳ねるため、上限が既定値で決まったレイアウトが一瞬見えてしまう。
  // 本アプリはブラウザのみで動作する SPA（サーバサイドレンダリングを行わない）ため、
  // useLayoutEffect の SSR 警告は発生しない。
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    // ResizeObserver 非対応環境では実測値を書き込まず、CSS 側の既定値へ委ねる。
    // 初回の実測値だけを固定してしまうと、その後の折り返し・省略表示の切り替え・
    // サブパネルの開閉に追従できず、パネルどうしが再び重なってしまうため。
    if (typeof ResizeObserver === "undefined") return undefined;

    const root = document.documentElement;
    const applySize = (): void => {
      const rect = element.getBoundingClientRect();
      // 小数寸法で 1px 足りずに重なる・折り返すことがないよう、切り上げた整数 px を渡す。
      const size = Math.ceil(dimension === "width" ? rect.width : rect.height);
      root.style.setProperty(variable, `${size}px`);
    };

    applySize();

    const observer = new ResizeObserver(applySize);
    observer.observe(element);
    return () => {
      observer.disconnect();
      // アンマウント後は CSS 側の既定値へ戻す。
      root.style.removeProperty(variable);
    };
  }, [ref, variable, dimension]);
}
